import React from "react";
import { 
  PlusIcon, 
  MinusIcon,
} from "@heroicons/react/24/outline";

import EtMagnetizationCollectiveIcon1SVGComp from "@/assets/img/icons/svgComp/EtMagnetizationCollectiveIcon1SVGComp";
import EtRoleCIsIcon1SVGComp from "@/assets/img/icons/svgComp/EtRoleCIsIcon1SVGComp";
import EtImprintMissionTeamIcon1SVGComp from "@/assets/img/icons/svgComp/EtImprintMissionTeamIcon1SVGComp";

import EtActionCollectiveIcon1SVGComp from "@/assets/img/icons/svgComp/EtActionCollectiveIcon1SVGComp";
import EtReflectionCollectiveIcon1SVGComp from "@/assets/img/icons/svgComp/EtReflectionCollectiveIcon1SVGComp";
import EtConversationCollectiveIcon1SVGComp from "@/assets/img/icons/svgComp/EtConversationCollectiveIcon1SVGComp";

const EtTrinityOnboardingUnlockTeams = () => {
  // Author - Atul Pandey
  // First Created - 15 Apr 2026
  // Purpose - Unlock Your Team section for Trinity onboarding
  // Updated - 16 Apr 2026 - Copy changes - Unlock Collective Growth Mindset - Collective Induction
  // Updated - 16 Apr 2026 - Copy changes - Unlock Collective Performance - Collective Actions 
  // Updated - 16 Apr 2026 - Copy changes - Unlock Collective Alignment - Collective Missions
  // Updated - 17 Apr 2026 - Copy changes - Unlock Collective Innovation - Collective Reflections
  // Updated - 17 Apr 2026 - Copy changes - Unlock Collective Impact - Collective Creations
  // Updated - 17 Apr 2026 - Copy changes - Unlock Collective Influence - Collective Conversations

  // 1 - State variable for show/hide toggle
  const [expandedCards, setExpandedCards] = React.useState({});

  // 2 - Handle ToggleCard function
  const toggleCard = (cardId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [cardId]: !prev[cardId],
    }));
  };

  // 3 - Unlock Your Teams Array
  const unlockYourTeams = [
    // 16 Apr 2026
    {
      id: "teamInduction",
      title: "Unlock Collective Growth Mindset",
      area: "Collective Induction",
      Icon: EtMagnetizationCollectiveIcon1SVGComp,
      detailType: "rich",
      intro:
        "Creating growth is a team sport, especially in tech services oriented organizations.",

      points: [
        () => (
          <p className="text-xs text-gray-600 leading-6">
            As a growth oriented CxO, your real superpower in driving sustained growth comes from how much your team shares that{" "}
            <span className="font-semibold text-orange-700 bg-orange-50 px-2 rounded-full">
              growth mindset
            </span>{" "}
            and how ready they are to act and add to that growth as a{" "}
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-lime-100 border border-lime-200 text-lime-800 font-semibold text-[11px]">
              collective
            </span>
            .
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            Therefore, as you unlock your own mindset, it must also connect to unlocking your{" "}
            <span className="font-semibold text-orange-700">
              team’s mindset
            </span>{" "}
            across the organization, starting with your{" "}
            <span className="font-semibold text-gray-800">
              next level leaders
            </span>
            .
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            Trinity’s Induction space is designed for this through two prime sources of{" "}
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-100 border border-orange-200 text-orange-800 font-semibold text-[11px]">
              collective induction
            </span>
            .
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            The first is a{" "}
            <span className="font-semibold text-orange-700 bg-orange-50 px-2 rounded-full">
              human advisor led Induction
            </span>{" "}
            where each member of your collective has their own space to unlock mindset and perspectives through self introspection, guided coach induction, and shared learning with their growth cohort inside the{" "}
            <span className="font-semibold text-gray-800">
              User’s Sessions
            </span>{" "}
            space.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            The second is the{" "}
            <span className="font-semibold text-orange-700 bg-orange-50 px-2 rounded-full">
              Sponsor’s Sessions
            </span>{" "}
            space where you directly engage your team through your own experiences, perspectives, and joint investigations in both{" "}
            <span className="font-semibold text-gray-800">
              1:1
            </span>{" "}
            and{" "}
            <span className="font-semibold text-gray-800">
              collective
            </span>{" "}
            settings.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            To keep this live and real, and not like traditional coaching where the session ends and action fades, Trinity allows you to{" "}
            <span className="font-semibold text-lime-700 bg-lime-50 px-2 rounded-full">
              assign and track planned actions
            </span>{" "}
            resulting from induction sessions so you can see where more nudge, support, or help is needed to unlock your team’s mindset further.
          </p>
        ),
      ],

      closing:
        "This collective induction space helps you go beyond individual insight and build a stronger growth mindset across your team in a visible, guided, and actionable way.",
    },
    // 16 Apr 2026
    {
      id: "collectiveActions",
      title: "Unlock Collective Performance",
      area: "Collective Actions",
      Icon: EtActionCollectiveIcon1SVGComp,
      detailType: "rich",
      intro:
        "Actions are the most tangible drivers of performance and growth, and their true power multiplies when executed as a collective.",

      points: [
        () => (
          <p className="text-xs text-gray-600 leading-6">
            While aspirations are easy to define, achieving bold goals requires more than intent. Trinity helps you turn actions into a{" "}
            <span className="font-semibold text-orange-700 bg-orange-50 px-2 rounded-full">
              high performance culture
            </span>{" "}
            by powering up execution across your{" "}
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-lime-100 border border-lime-200 text-lime-800 font-semibold text-[11px]">
              collective
            </span>
            .
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            Bold aspirations like building a strong pipeline of new clients are easy to state, but require deep orchestration across{" "}
            <span className="font-semibold text-gray-800">
              reachouts, conversations, proposals, and conversions
            </span>{" "}
            to truly materialize.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            These goals are rarely achieved individually. They operate like a{" "}
            <span className="font-semibold text-orange-700 bg-orange-50 px-2 rounded-full">
              relay race
            </span>{" "}
            where sustained pace and extended reach demand coordinated execution across multiple contributors.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            Trinity enables you to structure such missions as{" "}
            <span className="font-semibold text-orange-700">
              collective actions
            </span>{" "}
            where every team member becomes an equal partner in driving the outcome forward.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            You can define a shared action like{" "}
            <span className="font-semibold text-gray-800">
              50 new client reachouts in 30 days
            </span>{" "}
            and assign it across your team. Each member contributes through their own linked action, while results{" "}
            <span className="font-semibold text-lime-700 bg-lime-50 px-2 rounded-full">
              pool together
            </span>{" "}
            toward the same target.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            With Trinity’s{" "}
            <span className="font-semibold text-orange-700 bg-orange-50 px-2 rounded-full">
              Action Performance
            </span>{" "}
            view, you can track progress in real time, see contributions from each member, and build full visibility without relying on manual tracking or follow ups.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            This transparency allows you to{" "}
            <span className="font-semibold text-lime-700 bg-lime-50 px-2 rounded-full">
              recognize contributions
            </span>{" "}
            fairly, reduce subjectivity, and build a stronger culture of shared ownership and pride.
          </p>
        ),
      ],

      closing:
        "Collective actions help you move faster, achieve more together, and build a visible, high performance culture anchored in real contributions.",
    },
    // 16 Apr 2026
    {
      id: "missions",
      title: "Unlock Collective Alignment",
      area: "Collective Missions",
      Icon: EtImprintMissionTeamIcon1SVGComp,
      detailType: "rich",
      intro:
        "A culture of growth mindset requires a collective sense of ownership toward the missions that matter and the daily actions needed to achieve them together.",

      points: [
        () => (
          <p className="text-xs text-gray-600 leading-6">
            While actions determine achieved performance, they cannot fully activate without aligning the collective around the{" "}
            <span className="font-semibold text-orange-700 bg-orange-50 px-2 rounded-full">
              missions
            </span>{" "}
            that give those actions meaning, ownership, and pride.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            Trinity helps you as a CxO{" "}
            <span className="font-semibold text-gray-800">
              clearly articulate a powerful vision
            </span>{" "}
            that your next level leaders and teams can rally around with stronger trust, commitment, and shared belief.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            This alignment is primarily inducted through{" "}
            <span className="font-semibold text-orange-700 bg-orange-50 px-2 rounded-full">
              human led Induction
            </span>{" "}
            for your next level leaders and their teams, and is further strengthened through your own inductive space inside{" "}
            <span className="font-semibold text-gray-800">
              Sponsor’s Sessions
            </span>
            .
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            As clarity builds, Trinity helps you set these as{" "}
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-lime-100 border border-lime-200 text-lime-800 font-semibold text-[11px]">
              collective missions
            </span>{" "}
            for your next level and teams so that the backbone of growth becomes visible and shared.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            Missions form the{" "}
            <span className="font-semibold text-orange-700">
              highest level
            </span>{" "}
            of the Purpose Native backbone in Trinity, with each mission centered on bringing benefit to a defined{" "}
            <span className="font-semibold text-gray-800">
              constituency
            </span>
            .
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            Trinity is designed around five core constituencies to comprehensively cover the stakeholders you may serve to unlock a high growth culture:{" "}
            <span className="font-semibold text-gray-800">
              Clients, Org, Partners, Teams, and Self
            </span>
            . The first three are collective in nature and shared across your next level and the broader team.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            At the center of all these missions are the{" "}
            <span className="font-semibold text-lime-700 bg-lime-50 px-2 rounded-full">
              clients
            </span>
            . Yet building a true collective sense of mission is rarely straightforward. It takes deep deliberation, reflection, and often several iterations, which is precisely why Trinity’s Induction space exists.
          </p>
        ),
      ],

      closing:
        "Collective missions help transform aspiration into aligned ownership so your team can act with stronger commitment, clarity, and pride toward the growth outcomes that matter.",
    },
    // 17 Apr 2026
    {
      id: "teamReflections",
      // title: "Unlock Collective Intellect",
      title: "Unlock Collective Innovation",
      area: "Collective Reflections",
      Icon: EtReflectionCollectiveIcon1SVGComp,
      detailType: "rich",
      // intro:
      //   "The business of tech is a business of thought leadership, shaping your presence, influence, and trust with clients and partners.",
      intro:
           "The business of tech is a business of thought leadership and differentiation as an Innovator. It shapes up your presence, influence, and trust with your clients and partners.",

      points: [
        () => (
          <p className="text-xs text-gray-600 leading-6">
            Unlocking innovation is an act of unlocking your{" "}
            <span className="font-semibold text-orange-700 bg-orange-50 px-2 rounded-full">
              intellect
            </span>{" "}
            which begins with you, but sustained growth requires that intellect to{" "}
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-lime-100 border border-lime-200 text-lime-800 font-semibold text-[11px]">
              multiply across the organization
            </span>{" "}
            and evolve into a shared culture.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            Collective reflections enable your organization to{" "}
            <span className="font-semibold text-orange-700">
              differentiate itself
            </span>{" "}
            while deepening influence with clients through stronger, more thoughtful perspectives.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            In Trinity, reflections are not passive. As they build across your team, they become a{" "}
            <span className="font-semibold text-orange-700 bg-orange-50 px-2 rounded-full">
              living pool of insights
            </span>{" "}
            that your teams can continuously learn from and build upon.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            Your teams can{" "}
            <span className="font-semibold text-gray-800">
              glean insights from their cohort
            </span>{" "}
            and further refine, expand, and investigate them through collective introspection sessions.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            These reflections can directly power{" "}
            <span className="font-semibold text-lime-700 bg-lime-50 px-2 rounded-full">
              conversations, proposals, and pitches
            </span>{" "}
            with clients, strengthening presence and credibility.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            You can also objectively assess your teams on their{" "}
            <span className="font-semibold text-gray-800">
              evolving strengths
            </span>{" "}
            across key arenas based on how their thinking is progressing.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            Over time, these reflections can spawn{" "}
            <span className="font-semibold text-orange-700 bg-orange-50 px-2 rounded-full">
              collective intellectual creations
            </span>{" "}
            such as thought papers and sales collaterals, which can be published externally to further differentiate your brand.
          </p>
        ),
      ],

      closing:
        "Collective reflections help transform individual thinking into shared intellectual strength, enabling your organization to lead with stronger insight, influence, and differentiation.",
    },
    // 17 Apr 2026
    {
      id: "teamCreations",
      title: "Unlock Collective Impact",
      area: "Collective Creations",
      Icon: EtRoleCIsIcon1SVGComp,
      detailType: "rich",
      intro:
        "Building your thinking through reflections cannot sustain differentiation unless it is converted into reusable assets that propagate your thought leadership to wider audiences.",

      points: [
        () => (
          <p className="text-xs text-gray-600 leading-6">
            To drive sustained growth, your thought leadership must move beyond a{" "}
            <span className="font-semibold text-red-700 bg-red-50 px-2 rounded-full">
              one time impression
            </span>{" "}
            and become a{" "}
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-lime-100 border border-lime-200 text-lime-800 font-semibold text-[11px]">
              reusable asset
            </span>{" "}
            that delivers impact repeatedly.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            Your creations provide this{" "}
            <span className="font-semibold text-orange-700 bg-orange-50 px-2 rounded-full">
              leverage
            </span>{" "}
            by converting insights into outputs that can scale your presence, influence, and recall with clients.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            This is essential for building{" "}
            <span className="font-semibold text-gray-800">
              sustained high growth performance
            </span>{" "}
            where your impact must consistently reach and influence clients through your thinking.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            The impact of creations multiplies several folds when done as a{" "}
            <span className="font-semibold text-orange-700">
              collective
            </span>
            , making it critical to institutionalize a culture of{" "}
            <span className="font-semibold text-orange-700 bg-orange-50 px-2 rounded-full">
              collective creations
            </span>
            .
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            Trinity’s Creation Center enables this by giving you visibility into how each member contributes to the{" "}
            <span className="font-semibold text-gray-800">
              total creation footprint
            </span>{" "}
            over time, helping you build a culture rather than relying on push or mandate driven progress.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            You can objectively assess performance and{" "}
            <span className="font-semibold text-lime-700 bg-lime-50 px-2 rounded-full">
              reward higher contributions
            </span>{" "}
            while also designing targeted interventions to step up the creation quotient across the team.
          </p>
        ),
      ],

      closing:
        "Collective creations help transform your thought leadership into sustained impact, enabling your organization to scale influence, build recall, and drive consistent growth performance.",
    },
    // 17 Apr 2026
    {
      id: "teamConversations",
      title: "Unlock Collective Influence",
      area: "Collective Conversations",
      Icon: EtConversationCollectiveIcon1SVGComp,
      detailType: "rich",
      intro:
        "Your thinking and creations must translate into magnetic influence with clients to win new opportunities and drive growth.",

      points: [
        () => (
          <p className="text-xs text-gray-600 leading-6">
            Your ability to{" "}
            <span className="font-semibold text-orange-700 bg-orange-50 px-2 rounded-full">
              articulate, communicate, and convince
            </span>{" "}
            your clients is the ultimate factor in converting a growth promise into reality.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            This requires continuously developing your{" "}
            <span className="font-semibold text-gray-800">
              conversation muscle
            </span>{" "}
            not just individually, but as a{" "}
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-lime-100 border border-lime-200 text-lime-800 font-semibold text-[11px]">
              collective capability
            </span>
            .
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            Individual strength alone is not sufficient. Your conversations must be tested against{" "}
            <span className="font-semibold text-orange-700">
              diverse perspectives
            </span>{" "}
            and counter views to help you overcome headwinds and improve your{" "}
            <span className="font-semibold text-lime-700 bg-lime-50 px-2 rounded-full">
              opportunity conversion
            </span>
            .
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            Trinity enables this by allowing you to{" "}
            <span className="font-semibold text-orange-700 bg-orange-50 px-2 rounded-full">
              glean insights from conversations
            </span>{" "}
            across your cohort connected to the same contacts within the Conversations Center.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            You can also view{" "}
            <span className="font-semibold text-gray-800">
              contributions of each member
            </span>{" "}
            to identify who can provide deeper context and strengthen your engagement.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            This visibility allows you to objectively assess who is{" "}
            <span className="font-semibold text-lime-700 bg-lime-50 px-2 rounded-full">
              consistently investing
            </span>{" "}
            in building this capability across the team.
          </p>
        ),

        () => (
          <p className="text-xs text-gray-600 leading-6">
            The true power of collective conversations lies in using these insights in{" "}
            <span className="font-semibold text-orange-700">
              1:1 and collective inductive discussions
            </span>{" "}
            to investigate, critique, assimilate, and apply them toward stronger conversations, pitches, proposals, negotiations, and closures.
          </p>
        ),
      ],

      closing:
        "Collective conversations help you convert thinking into influence, enabling your team to engage with greater depth, confidence, and winning ability.",
    },
   
  ];

  return (
    <div>
      <p className="text-sm font-bold text-gray-800">Unlock Your Teams</p>
      <p className="text-xs text-gray-600 mt-1">
        Your growth can influence how your team and organization grow together.
      </p>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {unlockYourTeams.map(
          ({ id, title, area, Icon, detailType, intro, points, closing, desc }) => {
            const isExpanded = !!expandedCards[id];

            return (
              <div
                key={id}
                className="bg-gray-50 border border-gray-200 rounded p-4 h-full flex flex-col hover:bg-gray-100"
              >
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-start flex-1">
                    <div className="flex justify-center items-center w-10 h-10 rounded-full bg-white border border-orange-100 shadow-sm">
                      <Icon className="h-8 w-8 text-orange-600" />
                    </div>

                    <div className="ml-3">
                      <p className="text-sm font-bold text-gray-800">{title}</p>
                      <p className="text-[11px] font-semibold text-orange-700 mt-1 uppercase tracking-wide">
                        {area}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => toggleCard(id)}
                    className="ml-3 flex-shrink-0 rounded-full border border-gray-200 bg-white p-1 hover:bg-orange-50 hover:border-orange-200 transition"
                  >
                    {isExpanded ? (
                      <MinusIcon className="h-4 w-4 text-orange-600" />
                    ) : (
                      <PlusIcon className="h-4 w-4 text-orange-600" />
                    )}
                  </button>
                </div>

                {/* Rich content */}
                {detailType === "rich" ? (
                  <div className="mt-4 pl-12 flex-1">
                    <p className="text-xs text-gray-700 leading-6">{intro}</p>

                    {isExpanded && (
                      <>
                        <div className="mt-3 space-y-2">
                          {points.map((point, index) => (
                            <div key={index} className="flex items-start">
                              <span className="text-orange-600 text-xs font-bold mr-2 mt-1">
                                {index + 1}.
                              </span>

                              <div className="flex-1">
                                {typeof point === "function" ? (
                                  point()
                                ) : (
                                  <p className="text-xs text-gray-600 leading-6">
                                    {point}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="mt-3 bg-white border border-orange-100 rounded p-3 hover:bg-orange-100">
                          <p className="text-xs text-gray-700 leading-6">{closing}</p>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  isExpanded && (
                    <div className="mt-3 pl-12 flex-1">
                      <p className="text-xs text-gray-600 leading-6">{desc}</p>
                    </div>
                  )
                )}
              </div>
            );
          }
        )}
      </div>
    </div>
  );
};

export default EtTrinityOnboardingUnlockTeams;